/** TanStack Query hooks for engineering analysis. */

import { useMutation, useQuery } from '@tanstack/react-query'
import {
  getTransformerLoading,
  getFeederLoadingSummary,
  getPhaseBalance,
  getVoltageDropForFeeder,
  getVoltageDropSummary,
  getTransformerLoadingGeoJSON,
  getConductorVoltageDropGeoJSON,
  runWhatIfNewLoad,
} from '../api/engineering.ts'
import { getDataQuality } from '../api/dataQuality.ts'
import type { WhatIfNewLoadRequest, LoadSource } from '../types/engineering.ts'
import { runOpenDSS } from '../api/opendss.ts'
import type { OpenDSSRunParams } from '../types/opendss.ts'

export function useTransformerLoading(params?: {
  feeder_id?: number
  substation_id?: number
  avg_demand_kw?: number
  load_source?: LoadSource
  window_days?: number
}) {
  return useQuery({
    queryKey: ['engineering', 'transformer-loading', params],
    queryFn: () => getTransformerLoading(params!),
    staleTime: 60_000,
    enabled: params !== undefined,
  })
}

export function useFeederLoadingSummary(params?: {
  substation_id?: number
  avg_demand_kw?: number
  load_source?: LoadSource
  window_days?: number
}) {
  return useQuery({
    queryKey: ['engineering', 'feeder-loading', params],
    queryFn: () => getFeederLoadingSummary(params),
    staleTime: 60_000,
  })
}

export function usePhaseBalance(feederId: number | null, avgDemandKw?: number) {
  return useQuery({
    queryKey: ['engineering', 'phase-balance', feederId, avgDemandKw],
    queryFn: () => getPhaseBalance(feederId!, avgDemandKw),
    staleTime: 60_000,
    enabled: feederId !== null,
  })
}

export function useVoltageDrop(
  feederId: number | null,
  params?: { avg_demand_kw?: number; power_factor?: number },
) {
  return useQuery({
    queryKey: ['engineering', 'voltage-drop', feederId, params],
    queryFn: () => getVoltageDropForFeeder(feederId!, params),
    staleTime: 60_000,
    enabled: feederId !== null,
  })
}

export function useVoltageDropSummary(params?: {
  substation_id?: number
  avg_demand_kw?: number
}) {
  return useQuery({
    queryKey: ['engineering', 'voltage-drop-summary', params],
    queryFn: () => getVoltageDropSummary(params),
    staleTime: 60_000,
  })
}

export function useWhatIfNewLoad(feederId: number | null) {
  return useMutation({
    mutationFn: (body: WhatIfNewLoadRequest) => runWhatIfNewLoad(feederId!, body),
  })
}

export function useRunOpenDSS(feederId: number | null) {
  return useMutation({
    mutationFn: (params?: OpenDSSRunParams) => runOpenDSS(feederId!, params),
  })
}

export function useDataQuality(feederId: number | null) {
  return useQuery({
    queryKey: ['data-quality', feederId],
    queryFn: () => getDataQuality(feederId!),
    staleTime: 60_000,
    enabled: !!feederId,
  })
}

export function useTransformerLoadingGeoJSON(params?: {
  feeder_id?: number
  avg_demand_kw?: number
  load_source?: LoadSource
  window_days?: number
}) {
  return useQuery({
    queryKey: ['engineering', 'map', 'loading', params],
    queryFn: () => getTransformerLoadingGeoJSON(params!),
    staleTime: 60_000,
    enabled: params !== undefined,
  })
}

export function useConductorVoltageDropGeoJSON(params: {
  feeder_id: number
  avg_demand_kw?: number
  power_factor?: number
} | null) {
  return useQuery({
    queryKey: ['engineering', 'map', 'voltage-drop', params],
    queryFn: () => getConductorVoltageDropGeoJSON(params!),
    staleTime: 60_000,
    enabled: params !== null,
  })
}
